<template>
    <div class="my-cart">
        <Header @cart-ref="setCartRef" />
        <div class="main-content">
            <div class="cart-container">

                <h2 class="cart-title">Meu Carrinho</h2>

                <div class="cart-content">
                    <div class="cart-items-section">
                        <div class="select-all">
                            <label class="custom-checkbox">
                                <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                                <span class="checkmark"></span>
                                <span class="label-text">Todos os produtos</span>
                            </label>
                        </div>

                        <div v-if="cartItems.length === 0" class="empty-cart">
                            <i class="fas fa-shopping-cart"></i>
                            <p>Seu carrinho está vazio</p>
                            <router-link to="/library" class="btn-continue-shopping">
                                Continuar Comprando
                            </router-link>
                        </div>

                        <div v-else class="items-list">
                            <div v-for="item in cartItems" :key="getItemKey(item)" class="cart-item-card">
                                <div class="item-header">
                                    <label class="custom-checkbox">
                                        <input type="checkbox" :value="getItemKey(item)" v-model="selectedItems">
                                        <span class="checkmark"></span>
                                    </label>
                                    <span class="item-date">Adicionado em: {{ formatDate(item.addedAt) }}</span>
                                    <button class="btn-remove" @click="removeItem(item)">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>

                                <hr class="item-divider">

                                <div class="item-content">
                                    <img :src="item.url_imagem || require('@/assets/background/capa-padrao.png')"
                                        :alt="item.nome_disciplina">
                                    <div class="item-details">
                                        <h3>{{ item.nome_disciplina }}</h3>
                                        <p class="item-type">{{ formatItemType(item.tipo) }}</p>
                                        <p class="item-description">{{ item.descricao || 'Descrição não disponível' }}
                                        </p>
                                        <p class="item-price">R$ {{ formatPrice(item.preco) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="order-summary" v-if="cartItems.length > 0">
                        <h3>Resumo da Compra</h3>

                        <div class="license-config-panel">
                            <h4>Configuração da Licença Global</h4>

                            <div class="purchase-mode-toggles">
                                <label class="mode-label">
                                    <input type="radio" v-model="purchase_mode" value="exact" /> Acessos Exatos
                                </label>
                                <label class="mode-label" v-if="volumeTiers.length > 0">
                                    <input type="radio" v-model="purchase_mode" value="tier" /> Lote Fechado (Por Faixa)
                                </label>
                            </div>

                            <div v-if="purchase_mode === 'exact'" class="config-input-group">
                                <label>Quantidade Estimada (Alunos):</label>
                                <input type="number" v-model.number="accesses" min="1" class="glass-input" />
                            </div>

                            <div v-else class="config-input-group">
                                <label>Selecione a Faixa de Acessos:</label>
                                <select v-model.number="selected_tier_index" class="glass-input">
                                    <option v-for="(tier, index) in volumeTiers" :key="index" :value="index">
                                        {{ tier.min }} a {{ tier.max }} acessos ({{ tier.discount }}% OFF)
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="summary-items">
                            <div v-for="item in selectedCartItems" :key="getItemKey(item)" class="summary-item">
                                <span class="summary-item-name">{{ item.nome_disciplina }}</span>
                                <span class="summary-item-price">R$ {{ formatPrice(item.preco) }}</span>
                            </div>
                        </div>

                        <hr class="summary-divider">

                        <div class="summary-row">
                            <span>Volume de Acessos:</span>
                            <strong>{{ effectiveAccesses }} {{ purchase_mode === 'tier' ? '(Lote Fechado)' : ''
                                }}</strong>
                        </div>

                        <div class="summary-row">
                            <span>Subtotal Anual:</span>
                            <span>R$ {{ formatPrice(calculatedSubtotalAnnual) }}</span>
                        </div>

                        <div v-if="appliedVolumeDiscount > 0" class="summary-row discount-row">
                            <span>Desconto de Lote Anual ({{ appliedVolumeDiscount }}%):</span>
                            <span>- R$ {{ formatPrice(calculatedDiscountAmountAnnual) }}</span>
                        </div>

                        <div class="summary-row" style="color: #aaa; font-size: 0.85rem;">
                            <span>Total Líquido Anual:</span>
                            <span>R$ {{ selectedTotalPriceAnnual }}</span>
                        </div>

                        <hr class="summary-divider">

                        <div class="summary-total">
                            <span>Total Líquido Mensal (12x):</span>
                            <span class="total-price">R$ {{ selectedTotalPrice }}</span>
                        </div>
                        <button class="btn-continue" @click="continuePurchase" :disabled="selectedItems.length === 0">
                            Finalizar Licenciamento
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Header from '../components/Header.vue';
import requestV2 from '@/router/requestV2.js';

export default {
    name: 'MyCart',
    components: {
        Header,
    },
    data() {
        return {
            selectedItems: [],
            selectAll: false,
            cartComponent: null,
            cartItems: [],
            purchase_mode: 'exact',
            accesses: 100,
            selected_tier_index: 0,
            volumeTiers: []
        };
    },
    computed: {
        selectedCartItems() {
            return this.cartItems.filter(item =>
                this.selectedItems.includes(this.getItemKey(item))
            );
        },
        effectiveAccesses() {
            if (this.purchase_mode === 'tier' && this.volumeTiers.length > 0) {
                const tier = this.volumeTiers[this.selected_tier_index];
                if (tier) return tier.max;
            }
            return this.accesses || 1;
        },
        appliedVolumeDiscount() {
            if (!this.volumeTiers || this.volumeTiers.length === 0) return 0;
            if (this.purchase_mode === 'tier') {
                const tier = this.volumeTiers[this.selected_tier_index];
                return tier ? (tier.discount || 0) : 0;
            }
            const accesses = this.effectiveAccesses;
            const tier = this.volumeTiers.find(t => accesses >= t.min && accesses <= t.max);
            if (tier) return tier.discount || 0;
            const lastTier = this.volumeTiers[this.volumeTiers.length - 1];
            if (accesses > lastTier.max) return lastTier.discount || 0;
            return 0;
        },
        calculatedSubtotalAnnual() {
            const sumItemsPrice = this.selectedCartItems.reduce((sum, item) => {
                const price = parseFloat(item.preco) || 0;
                return sum + price;
            }, 0);
            return sumItemsPrice * this.effectiveAccesses;
        },
        calculatedDiscountAmountAnnual() {
            return this.calculatedSubtotalAnnual * (this.appliedVolumeDiscount / 100);
        },
        selectedTotalPriceAnnual() {
            const finalTotal = this.calculatedSubtotalAnnual - this.calculatedDiscountAmountAnnual;
            return finalTotal.toFixed(2).replace('.', ',');
        },
        calculatedSubtotal() {
            return this.calculatedSubtotalAnnual / 12;
        },
        calculatedDiscountAmount() {
            return this.calculatedSubtotal * (this.appliedVolumeDiscount / 100);
        },
        selectedTotalPrice() {
            const finalTotal = this.calculatedSubtotal - this.calculatedDiscountAmount;
            return finalTotal.toFixed(2).replace('.', ',');
        }
    },
    methods: {
        async loadCart() {
            try {
                const response = await requestV2.get('/cart');
                this.cartItems = response.data;
            } catch (e) {
                console.error('Erro ao carregar carrinho da API:', e);
                this.cartItems = [];
            }
        },
        getItemKey(item) {
            if (item.isPacote || item.tipo === 'pacote-completo') {
                return `pacote-${item.codigo_disciplina}`;
            }
            return `${item.codigo_disciplina}-${item.tipo || 'individual'}`;
        },
        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedItems = this.cartItems.map(item => this.getItemKey(item));
            } else {
                this.selectedItems = [];
            }
        },
        formatDate(dateString) {
            if (!dateString) return 'Data não disponível';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        },
        formatItemType(tipo) {
            const tipos = {
                'material-didatico': 'Material Didático',
                'video-aula': 'Vídeo Aula',
                'atividade': 'Atividade',
                'pacote-completo': 'Pacote Completo',
                'pacote': 'Pacote Completo'
            };
            return tipos[tipo] || tipo;
        },
        formatPrice(preco) {
            if (!preco) return '0,00';
            return parseFloat(preco).toFixed(2).replace('.', ',');
        },
        async removeItem(item) {
            try {
                await requestV2.delete(`/cart/${this.getItemKey(item)}`);
                await this.loadCart();
                this.selectedItems = this.selectedItems.filter(key => key !== this.getItemKey(item));

                // Dispara o evento customizado para que o ShoppingCart se atualize também
                window.dispatchEvent(new Event('cart-updated'));
            } catch (e) {
                console.error('Erro ao remover item:', e);
            }
        },
        continuePurchase() {
            if (this.selectedItems.length === 0) {
                alert('Selecione pelo menos um item para continuar');
                return;
            }

            const config = {
                purchase_mode: this.purchase_mode,
                accesses: this.accesses,
                effectiveAccesses: this.effectiveAccesses,
                appliedVolumeDiscount: this.appliedVolumeDiscount,
                calculatedSubtotal: this.calculatedSubtotal,
                calculatedDiscountAmount: this.calculatedDiscountAmount,
                selectedTotalPrice: this.selectedTotalPrice,
                calculatedSubtotalAnnual: this.calculatedSubtotalAnnual,
                calculatedDiscountAmountAnnual: this.calculatedDiscountAmountAnnual,
                selectedTotalPriceAnnual: this.selectedTotalPriceAnnual
            };
            localStorage.setItem('cartLicenseConfig', JSON.stringify(config));
            localStorage.setItem('selectedCartItems', JSON.stringify(this.selectedItems));

            // Redirecionar para página de pagamento
            console.log('Itens selecionados para compra:', this.selectedCartItems);
            this.$router.push('/checkout');
        },
        setCartRef(cartRef) {
            this.cartComponent = cartRef;
        }
    },
    watch: {
        selectedItems(newVal) {
            this.selectAll = newVal.length === this.cartItems.length && this.cartItems.length > 0;
        }
    },
    async mounted() {
        await this.loadCart();

        try {
            const response = await requestV2.get('/pricing');
            if (response.data && response.data.volumeTiers) {
                this.volumeTiers = response.data.volumeTiers;
            }
        } catch (err) {
            console.error('Erro ao carregar faixas de preço da API:', err);
            const savedTiers = localStorage.getItem('admin_tiers');
            if (savedTiers) {
                try {
                    this.volumeTiers = JSON.parse(savedTiers);
                } catch (e) {
                    console.error('Erro ao ler faixas', e);
                }
            }
        }

        // Escuta eventos customizados
        window.addEventListener('cart-updated', this.loadCart);
    },
    beforeUnmount() {
        window.removeEventListener('cart-updated', this.loadCart);
    }
};
</script>

<style scoped src="../styles/MyCart.css" />